import { usePlaybackPlay } from '@app/core/playback/hooks/usePlayback';
import { usePostHogCaptureLiveTopics } from '@app/modules/logger/screens/stream/features/live-topics/hooks/usePostHogCaptureLiveTopics';
import { nlpIntegration } from '@mi-front/api-clients';
import { minsToMs } from '@mi-front/core';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useGoToTime } from '../../../../hooks/cursor/useGoToTime';
import { LiveTopicNavigation } from './LiveTopicNavigation';

const goToMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.goTo',
  defaultMessage: 'Ir',
});

/**
 * Actions for the live topic detail: "Ir" (go to time), play, and prev/next topic navigation.
 */
export function LiveTopicActions({
  topic,
  topics,
  onSelectTopic,
}: {
  topic: nlpIntegration.TopicOutDto;
  topics: nlpIntegration.TopicOutDto[];
  onSelectTopic: (id: string) => void;
}) {
  const { $t } = useIntl();
  const capture = usePostHogCaptureLiveTopics();
  const { goToTime } = useGoToTime({ focusOffsetMs: minsToMs(2) });
  const play = usePlaybackPlay();

  const hasStartTime = !!topic.start_time;
  const topicStart = DateTime.fromISO(topic.start_time!);

  const handleGoTo = useCallback(() => {
    if (!hasStartTime) {
      return;
    }

    capture('go_to_time_clicked', { topic_id: topic.id, source: 'panel_detail' });
    goToTime(topicStart.toMillis());
    play?.({
      startTs: topicStart.toMillis(),
    });
  }, [hasStartTime, topic.id, topic.start_time, goToTime, play, capture]);

  const handlePlay = useCallback(() => {
    capture('play_from_topic_clicked', { topic_id: topic.id, source: 'panel_detail' });
    goToTime(topicStart.toMillis());
    play?.({
      startTs: topicStart.toMillis(),
    });
  }, [topic.id, goToTime, play, capture]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tooltip title={$t(goToMessage)}>
        <IconButton size="small" color="secondary" onClick={handleGoTo} disabled={!hasStartTime}>
          <LocationSearchingIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <IconButton size="small" color="secondary" onClick={handlePlay}>
        <PlayCircleOutlineRoundedIcon fontSize="inherit" />
      </IconButton>
      <LiveTopicNavigation topic={topic} topics={topics} onSelectTopic={onSelectTopic} />
    </Stack>
  );
}
